import { useIntl } from "react-intl";
import { useLocation } from "react-router";
import { Button } from "@oai/platform/ui/Button";
import { Check, Language } from "@oai/platform/ui/Icon";
import { Menu } from "@oai/platform/ui/Menu";

import { CookiePreferencesFooterTrigger } from "@/components/common/CookiePreferences";
import {
  AVAILABLE_LOCALES,
  DEFAULT_LOCALE,
  getLocaleDisplayName,
  getLocalizedLandingPath,
  LOCALE_SEARCH_PARAM,
  sanitizeLocale,
  setLocalePreferenceCookie,
} from "@/locale";
import { messages } from "@/localization/Landing.messages";

import type { Location } from "react-router";
import type { LocaleKey } from "@/locale";

function getLocaleHref(location: Location, locale: LocaleKey): string {
  const searchParams = new URLSearchParams(location.search);
  searchParams.delete(LOCALE_SEARCH_PARAM);
  const search = searchParams.toString();
  return `${getLocalizedLandingPath(locale)}${search ? `?${search}` : ""}${location.hash}`;
}

export function LandingFooter() {
  const intl = useIntl();
  const location = useLocation();
  const selectedLocale = sanitizeLocale(intl.locale) ?? DEFAULT_LOCALE;

  return (
    <footer className="mx-auto w-full max-w-[1280px] px-6 pt-16 pb-8 text-sm md:pt-24">
      <div className="flex w-full flex-col items-start gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-secondary">
            {intl.formatMessage(messages.copyright, { year: new Date().getFullYear() })}
          </span>
          <CookiePreferencesFooterTrigger />
        </div>
        <div className="self-end sm:self-auto">
          <Menu>
            <Menu.Trigger>
              <Button
                aria-label={intl.formatMessage(messages.language)}
                color="secondary"
                iconSize="md"
                size="sm"
                title={intl.formatMessage(messages.language)}
                variant="ghost"
              >
                <Language />
                {getLocaleDisplayName(selectedLocale)}
              </Button>
            </Menu.Trigger>
            <Menu.Content align="end" maxHeight={360} minWidth={260} side="top" width={260}>
              {AVAILABLE_LOCALES.map((locale) => {
                const isSelected = locale === selectedLocale;

                return (
                  <Menu.Link
                    key={locale}
                    aria-current={isSelected ? "true" : undefined}
                    as="a"
                    className="px-2 py-2"
                    href={getLocaleHref(location, locale)}
                    lang={locale}
                    onClick={() => setLocalePreferenceCookie(locale)}
                  >
                    <span className="flex w-full items-center justify-between gap-3">
                      <span className="truncate">{getLocaleDisplayName(locale)}</span>
                      {isSelected ?
                        <Check aria-hidden="true" className="icon-primary size-4 shrink-0" />
                      : null}
                    </span>
                  </Menu.Link>
                );
              })}
            </Menu.Content>
          </Menu>
        </div>
      </div>
    </footer>
  );
}
